import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useTranslation } from 'react-i18next';
import { useLocationStore } from '@/features/location/store/location-store';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT, MARKER_MIN_ZOOM } from '@/utils/constants';
import { useCountryCounts } from '../hooks/useCountryCounts';
import type { CountryMetrics } from '../api/country-counts-api';
import {
  easeOutCubic,
  hoverStyleAt,
  isoToFlagEmoji,
  lerp,
  restingStyle,
} from '../utils';

const FALLBACK_HIGHLIGHT_COLOR = '#111827';
const DEFAULT_FILL_COLOR = '#3b82f6';
const DEFAULT_FILL_OPACITY = 0.08;

const COUNTRY_VIEW_MAX_ZOOM = MARKER_MIN_ZOOM - 1;
const GEOJSON_URL = '/data/countries-50m.geojson';
const SKIP_ISO_A2 = new Set(['AQ', 'RU', 'UA', 'CN', 'CN-TW', 'IL', 'PS']);

// Hover highlight animation timings (Data layer has no CSS transitions,
// so we interpolate the style via requestAnimationFrame).
const HOVER_IN_MS = 180;
const HOVER_OUT_MS = 140;

// Estimated tooltip box for viewport-edge clamping
const TIP_WIDTH = 230;
const TIP_HEIGHT = 132;
const TIP_OFFSET = 14;

// Soft radial halo that follows the cursor while a country is highlighted
const GLOW_SIZE = 36;

// A press held longer than this (e.g. touch-and-hold to read the tooltip) is
// treated as "view details", not a tap — so it doesn't zoom in on release.
const LONG_PRESS_MS = 300;

// On touch, wait this long before showing a country's details (highlight +
// card). A pan's dragstart fires within this window and cancels the reveal, so
// starting a drag never flashes the highlight. Matches LONG_PRESS_MS so the
// model is consistent: hold to peek, quicker = tap/drag. Desktop is instant.
const TOUCH_SHOW_DELAY_MS = LONG_PRESS_MS;

type HoverState = {
  iso: string;
  name: string;
  x: number;
  y: number;
};

interface CountryFeature {
  properties?: { iso_a2?: string };
}
interface CountryFeatureCollection {
  type: string;
  features: CountryFeature[];
}

export function CountryOverlay() {
  const { t } = useTranslation();
  const map = useMap();
  const zoom = useLocationStore((s) => s.zoomLevel);
  const brandColor = useOperatorStore((s) => s.theme?.primary_color);
  const urlHighlightColor = useUiModeStore((s) => s.countryHighlightColor);
  const urlFillColor = useUiModeStore((s) => s.countryFillColor);
  const urlFillOpacity = useUiModeStore((s) => s.countryFillOpacity);
  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
  const [hovered, setHovered] = useState<HoverState | null>(null);

  // Highlight color priority: ?country_highlight_color → operator brand → fallback.
  const highlightColor =
    urlHighlightColor || brandColor || FALLBACK_HIGHLIGHT_COLOR;
  // Resting fill tint for hoverable countries: ?country_fill_color → default.
  const fillColor = urlFillColor || DEFAULT_FILL_COLOR;
  // Resting fill opacity: ?country_fill_opacity → default.
  const fillOpacity = urlFillOpacity ?? DEFAULT_FILL_OPACITY;

  const dataRef = useRef<google.maps.Data | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const geoJsonLoadedRef = useRef(false);
  // Timestamp of the last press start, to distinguish a tap from a long hold.
  const pressStartRef = useRef(0);

  const shouldShow = zoom <= COUNTRY_VIEW_MAX_ZOOM;

  // Per-country metrics from the BE. Only fetched while the overlay is showing.
  const { data: countries, isLoading } = useCountryCounts(shouldShow);
  const countsMap = useMemo(
    () =>
      new Map<string, CountryMetrics>(
        (countries ?? []).map((c) => [c.code_2.toUpperCase(), c])
      ),
    [countries]
  );

  // Refs so the imperative Data-layer callbacks always read current values.
  const countsRef = useRef(countsMap);
  const countsLoadedRef = useRef(false);
  const highlightColorRef = useRef<string>(highlightColor);
  useEffect(() => {
    highlightColorRef.current = highlightColor;
  }, [highlightColor]);
  // Resting fill color + opacity read by the imperative style callback.
  const fillColorRef = useRef<string>(fillColor);
  const fillOpacityRef = useRef<number>(fillOpacity);

  // Whether the map is being panned — suppress hover while dragging so a pan
  // over countries doesn't flash their details.
  const draggingRef = useRef(false);
  // Pending touch show-delay timer (cancelled by a drag) — see mouseover.
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Teardown for the native pointer listeners used to detect drags.
  const pointerCleanupRef = useRef<(() => void) | null>(null);
  // Whether a pointer is currently pressed down on the map (touch/mouse).
  const pointerDownRef = useRef(false);
  // Read the breakpoint inside imperative listeners without stale closures.
  const isDesktopRef = useRef(isDesktop);
  useEffect(() => {
    isDesktopRef.current = isDesktop;
  }, [isDesktop]);

  // A country is hidden (invisible, non-interactive) when we have a non-empty
  // coverage dataset and it's absent from it. If counts haven't loaded, errored,
  // or came back empty, we hide nothing — so zooming out always shows borders
  // rather than a blank map.
  const isHidden = useCallback(
    (iso: string) =>
      countsLoadedRef.current &&
      countsRef.current.size > 0 &&
      !countsRef.current.has(iso),
    []
  );

  // (Re)apply resting styles to every feature — call whenever coverage or the
  // fill color changes.
  const applyResting = useCallback(() => {
    dataRef.current?.setStyle((feature) => {
      const iso = (feature.getProperty('iso_a2') as string) || '';
      return restingStyle(
        isHidden(iso),
        fillColorRef.current,
        fillOpacityRef.current
      );
    });
  }, [isHidden]);

  // Re-style when the resting fill color or opacity changes.
  useEffect(() => {
    fillColorRef.current = fillColor;
    fillOpacityRef.current = fillOpacity;
    applyResting();
  }, [fillColor, fillOpacity, applyResting]);

  // Single in-flight highlight animation.
  const animRef = useRef<{
    id: number;
    feature: google.maps.Data.Feature | null;
    t: number;
  }>({ id: 0, feature: null, t: 0 });

  // Last halo position — suppress the trailing transition on large jumps.
  const lastGlowPos = useRef<{ x: number; y: number } | null>(null);

  // Keep refs in sync with the latest query result and re-style the layer.
  useEffect(() => {
    countsRef.current = countsMap;
    countsLoadedRef.current = countries !== undefined;
    applyResting();
  }, [countsMap, countries, applyResting]);

  useEffect(() => {
    if (!map) return;

    if (!shouldShow) {
      if (dataRef.current) dataRef.current.setMap(null);
      setHovered(null);
      return;
    }

    if (!dataRef.current) {
      const layer = new google.maps.Data({ map });
      dataRef.current = layer;
      applyResting();

      const animateFeature = (
        feature: google.maps.Data.Feature,
        dir: 'in' | 'out'
      ) => {
        const anim = animRef.current;
        cancelAnimationFrame(anim.id);

        // Hovering a new country mid-animation: snap the old one back.
        if (anim.feature && anim.feature !== feature) {
          layer.revertStyle(anim.feature);
          anim.t = 0;
        }
        anim.feature = feature;

        const color = highlightColorRef.current;
        const fromT = anim.t;
        const toT = dir === 'in' ? 1 : 0;
        const duration = dir === 'in' ? HOVER_IN_MS : HOVER_OUT_MS;
        const start = performance.now();

        const frame = (now: number) => {
          const p = Math.min(1, (now - start) / duration);
          const t = lerp(fromT, toT, easeOutCubic(p));
          anim.t = t;
          layer.overrideStyle(feature, hoverStyleAt(t, color));
          if (p < 1) {
            anim.id = requestAnimationFrame(frame);
          } else if (dir === 'out') {
            layer.revertStyle(feature);
            anim.feature = null;
            anim.t = 0;
          }
        };
        anim.id = requestAnimationFrame(frame);
      };

      const showFeature = (
        feature: google.maps.Data.Feature,
        x: number,
        y: number
      ) => {
        const iso = feature.getProperty('iso_a2') as string;
        const name = (feature.getProperty('name') as string) || iso;
        animateFeature(feature, 'in');
        setHovered({ iso, name, x, y });
      };

      const clearShowTimer = () => {
        if (showTimerRef.current) {
          clearTimeout(showTimerRef.current);
          showTimerRef.current = null;
        }
      };

      const lOver = layer.addListener(
        'mouseover',
        (e: google.maps.Data.MouseEvent) => {
          // Never reveal details mid-pan.
          if (draggingRef.current) return;
          const feature = e.feature;
          const dom = (e as unknown as { domEvent: MouseEvent }).domEvent;
          const { clientX: x, clientY: y } = dom;

          // Desktop hover is instant; touch waits briefly so a pan that starts
          // right after the finger lands cancels the reveal (no flash).
          clearShowTimer();
          if (isDesktopRef.current) {
            // Only reveal on a genuine hover — not while a button is held (the
            // start of a pan or a click-to-zoom), which would flash the bloom.
            if (!pointerDownRef.current) showFeature(feature, x, y);
          } else {
            // Touch: only reveal while the finger is still held down and
            // stationary. This ignores the synthesized mouseover Maps fires
            // after a drag/tap is released (the "flash on let go").
            showTimerRef.current = setTimeout(() => {
              showTimerRef.current = null;
              if (!draggingRef.current && pointerDownRef.current) {
                showFeature(feature, x, y);
              }
            }, TOUCH_SHOW_DELAY_MS);
          }
        }
      );
      const lOut = layer.addListener(
        'mouseout',
        (e: google.maps.Data.MouseEvent) => {
          clearShowTimer();
          setHovered(null);
          // Only fade out a feature that's actually highlighted. Otherwise just
          // restore its resting style — animating "out" from t=0 would briefly
          // paint the brand color (hoverStyleAt(0) is brand-tinted), which
          // showed up as a flash on borders crossed mid-drag and on release.
          if (animRef.current.feature === e.feature) {
            animateFeature(e.feature, 'out');
          } else {
            layer.revertStyle(e.feature);
          }
        }
      );
      const lMove = layer.addListener(
        'mousemove',
        (e: google.maps.Data.MouseEvent) => {
          if (draggingRef.current) return;
          const dom = (e as unknown as { domEvent: MouseEvent }).domEvent;
          setHovered((prev) =>
            prev ? { ...prev, x: dom.clientX, y: dom.clientY } : prev
          );
        }
      );
      const lDown = layer.addListener('mousedown', () => {
        pressStartRef.current = performance.now();
      });
      const cancelActiveHover = () => {
        clearShowTimer();
        // Stop the running animation frame first — otherwise its rAF loop keeps
        // re-painting the highlight after we revert, which reads as a flash on
        // the country being dragged.
        cancelAnimationFrame(animRef.current.id);
        if (animRef.current.feature) {
          layer.revertStyle(animRef.current.feature);
          animRef.current.feature = null;
          animRef.current.t = 0;
        }
        setHovered(null);
      };

      // Suppress hover while the map is being panned. Maps' dragstart covers
      // pans that begin on empty map area...
      const lDragStart = map.addListener('dragstart', () => {
        draggingRef.current = true;
        cancelActiveHover();
      });
      const lDragEnd = map.addListener('dragend', () => {
        draggingRef.current = false;
      });

      // ...but a pan that begins on a (clickable) country may not fire
      // dragstart, so detect movement directly from pointer events on the map
      // container. This is what reliably stops the highlight flashing mid-drag.
      // Listen on window (capture) rather than the map container: during a drag
      // Maps captures the pointer on an inner element, so a container listener
      // can miss the moves and never flip draggingRef. Window/capture always
      // sees them. Scope the press to drags that begin on the map.
      const container = map.getDiv();
      let downPos: { x: number; y: number } | null = null;
      const onPointerDown = (ev: PointerEvent) => {
        if (!container.contains(ev.target as Node)) return;
        downPos = { x: ev.clientX, y: ev.clientY };
        draggingRef.current = false;
        pointerDownRef.current = true;
      };
      const onPointerMove = (ev: PointerEvent) => {
        if (!downPos || draggingRef.current) return;
        if (Math.hypot(ev.clientX - downPos.x, ev.clientY - downPos.y) > 8) {
          draggingRef.current = true;
          cancelActiveHover();
        }
      };
      const onPointerEnd = () => {
        downPos = null;
        draggingRef.current = false;
        pointerDownRef.current = false;
      };
      window.addEventListener('pointerdown', onPointerDown, true);
      window.addEventListener('pointermove', onPointerMove, true);
      window.addEventListener('pointerup', onPointerEnd, true);
      window.addEventListener('pointercancel', onPointerEnd, true);
      pointerCleanupRef.current = () => {
        window.removeEventListener('pointerdown', onPointerDown, true);
        window.removeEventListener('pointermove', onPointerMove, true);
        window.removeEventListener('pointerup', onPointerEnd, true);
        window.removeEventListener('pointercancel', onPointerEnd, true);
      };
      const lClick = layer.addListener(
        'click',
        (e: google.maps.Data.MouseEvent) => {
          const start = pressStartRef.current;
          pressStartRef.current = 0;
          // Long hold (e.g. touch-and-hold to read the tooltip) = view, not zoom.
          if (start && performance.now() - start > LONG_PRESS_MS) return;
          if (!e.latLng) return;
          map.panTo(e.latLng);
          map.setZoom(MARKER_MIN_ZOOM);
          setHovered(null);
        }
      );
      listenersRef.current = [
        lOver,
        lOut,
        lMove,
        lDown,
        lClick,
        lDragStart,
        lDragEnd,
      ];
    } else {
      dataRef.current.setMap(map);
    }

    if (!geoJsonLoadedRef.current) {
      geoJsonLoadedRef.current = true;
      fetch(GEOJSON_URL)
        .then((r) => r.json() as Promise<CountryFeatureCollection>)
        .then((geojson) => {
          const filtered = {
            ...geojson,
            features: geojson.features.filter(
              (f) => !SKIP_ISO_A2.has(f.properties?.iso_a2 ?? '')
            ),
          };
          dataRef.current?.addGeoJson(filtered);
        })
        .catch((err) => {
          console.error('[CountryOverlay] failed to load GeoJSON', err);
          geoJsonLoadedRef.current = false;
        });
    }
  }, [map, shouldShow, applyResting, isHidden]);

  useEffect(() => {
    // animRef.current is a stable object (only its fields mutate), so capturing
    // it is safe; listeners are added later so they must be read at unmount.
    const anim = animRef.current;
    return () => {
      cancelAnimationFrame(anim.id);
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      pointerCleanupRef.current?.();
      pointerCleanupRef.current = null;
      if (dataRef.current) dataRef.current.setMap(null);
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [];
      dataRef.current = null;
    };
  }, []);

  if (!hovered || !shouldShow) {
    lastGlowPos.current = null;
    return null;
  }

  const accent = highlightColor;
  const flag = isoToFlagEmoji(hovered.iso);
  const metrics = countsMap.get(hovered.iso);
  const loaded = countries !== undefined;

  const card = (
    <div
      key={hovered.iso}
      className="animate-in fade-in zoom-in-95 duration-200 rounded-xl bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200 overflow-hidden"
      style={{ borderTopWidth: 3, borderTopColor: accent }}
    >
      <div className="px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          {flag && (
            <span className="text-xl leading-none" aria-hidden>
              {flag}
            </span>
          )}
          <span className="font-semibold text-gray-900 truncate">
            {hovered.name}
          </span>
        </div>

        {metrics ? (
          <>
            <div className="mt-1.5 flex items-baseline gap-1.5">
              <span className="text-lg font-bold tabular-nums text-gray-900">
                ~{metrics.charge_points.toLocaleString()}
              </span>
              <span className="text-xs text-gray-500">
                {t('countryOverlay.evse')}
              </span>
            </div>
            <div className="mt-0.5 text-xs text-gray-500 tabular-nums">
              ~{metrics.locations.toLocaleString()}{' '}
              {t('countryOverlay.locations', { count: metrics.locations })}
            </div>
          </>
        ) : (
          <div className="mt-1.5 text-sm text-gray-400">
            {!loaded && isLoading ? t('common.loading') : '—'}
          </div>
        )}

        <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
          {metrics ? (
            <span>
              {isDesktop
                ? t('countryOverlay.clickToExplore')
                : t('countryOverlay.tapToExplore')}
            </span>
          ) : (
            <span />
          )}
          <span className="font-mono">{hovered.iso}</span>
        </div>
      </div>
    </div>
  );

  // On mobile, anchor the details to the bottom of the screen so they're never
  // pushed off-screen or hidden under the finger.
  if (!isDesktop) {
    return (
      <div className="pointer-events-none fixed inset-x-3 bottom-3 z-50">
        {card}
      </div>
    );
  }

  // Desktop: follow the cursor, with a soft halo. Clamp fully inside the
  // viewport (flip past the cursor near an edge, then bound both axes).
  const MARGIN = 8;
  let left = hovered.x + TIP_OFFSET;
  if (left + TIP_WIDTH > window.innerWidth - MARGIN) {
    left = hovered.x - TIP_OFFSET - TIP_WIDTH;
  }
  left = Math.max(
    MARGIN,
    Math.min(left, window.innerWidth - TIP_WIDTH - MARGIN)
  );

  let top = hovered.y + TIP_OFFSET;
  if (top + TIP_HEIGHT > window.innerHeight - MARGIN) {
    top = hovered.y - TIP_OFFSET - TIP_HEIGHT;
  }
  top = Math.max(
    MARGIN,
    Math.min(top, window.innerHeight - TIP_HEIGHT - MARGIN)
  );

  // Trail only for small cursor movements; snap on jumps (> 80 px)
  const prevGlow = lastGlowPos.current;
  const glowJumped =
    !prevGlow ||
    Math.hypot(hovered.x - prevGlow.x, hovered.y - prevGlow.y) > 80;
  lastGlowPos.current = { x: hovered.x, y: hovered.y };

  return (
    <>
      {/* Cursor halo — outer div positions, inner div animates (keeps the
          enter keyframe from overriding the positioning transform). */}
      <div
        className="pointer-events-none fixed left-0 top-0 z-40"
        style={{
          width: GLOW_SIZE,
          height: GLOW_SIZE,
          transform: `translate(${hovered.x - GLOW_SIZE / 2}px, ${hovered.y - GLOW_SIZE / 2}px)`,
          transition: glowJumped ? 'none' : 'transform 90ms ease-out',
        }}
      >
        <div
          className="w-full h-full animate-in fade-in duration-300"
          style={{
            opacity: 0.22,
            background: `radial-gradient(circle, ${accent} 0%, transparent 70%)`,
          }}
        />
      </div>

      <div
        className="pointer-events-none fixed z-50"
        style={{ left, top, width: TIP_WIDTH }}
      >
        {card}
      </div>
    </>
  );
}
